/**
 * Soul Duel — Plan 4b Task 9.1 (cast) + Plan 11c Task 1.3 (flag-off fallback)
 *
 * Spec §4 #14: Triggers a retro-minigame duel between caster and target.
 * Cast: picks a random minigame, sets pendingSoulDuel, emits events.
 * Resolution: handled by soul-duel-handler.ts (Task 9.2).
 * Defender Stone Skin blocks the hostile duel trigger before RNG or fallback damage.
 *
 * Flag-off fallback (MINIGAMES_ENABLED=false or 'soul-duel' not in MINIGAMES_AVAILABLE):
 *   66% chance: attacker wins (target takes lethal damage via applyDamage, source='soul_duel').
 *   33% chance: attacker loses (caster takes lethal damage via applyDamage, source='soul_duel_backfire').
 *   Uses Math.random (intentionally unseeded — fair coin flip, not reproducible RNG).
 *   pendingSoulDuel stays null; no soul_duel_triggered event.
 *
 * handleDeath pattern: item handlers call applyDamage only (matching thunder-fist and
 * orbital-cannon patterns). The game loop (landing-effects / executeTurn) handles
 * post-death respawn via handleDeath. Item cast functions do NOT call handleDeath directly
 * because they lack access to graph and distanceCache.
 *
 * Self-registers via addItem(soulDuel) at module load.
 */
import type { GameState } from '../types';
import type { ItemCastCtx, ItemCastResult, Item } from './types';
import { appendEvent } from '../event-log';
import { mulberry32 } from '../dice';
import { addItem } from './registry';
import { isMinigameEnabled } from '@shared/config/features';
import { applyDamage } from '../damage';
import { tryBlockWithStoneSkin } from '../stone-skin-block';

/** Retro-minigame pool for Soul Duel (Plans 25-32 territory; Plan 4b placeholder). */
export const SOUL_DUEL_MINIGAME_POOL: readonly string[] = ['retro_pong', 'retro_frogger'];

// ---------------------------------------------------------------------------
// UI telegraph — description string (computed at module load)
// ---------------------------------------------------------------------------

export const FLAG_OFF_DESCRIPTION =
  'Soul Duel — challenge a target to a soul duel. ' +
  '66% chance to defeat them. 33% chance to defeat yourself. Both outcomes are final.';

export const FLAG_ON_DESCRIPTION =
  'Soul Duel — challenge a target to a portal duel. Winner survives. Loser is defeated.';

const description = isMinigameEnabled('soul-duel') ? FLAG_ON_DESCRIPTION : FLAG_OFF_DESCRIPTION;

export const soulDuel: Item = {
  id: 'soul_duel',
  name: 'Soul Duel',
  description,
  targetType: 'player',
  category: 'armory',
  rarity: 'rare',
  blockedByStoneSkin: true,

  cast(state: GameState, ctx: ItemCastCtx): ItemCastResult {
    const { casterId } = ctx;
    const targetId = ctx.target.targetPlayerId;

    // Step 1: Validate
    if (!targetId) throw new Error('Soul Duel: missing targetPlayerId in ctx.target');
    const target = state.players.get(targetId);
    if (!target) throw new Error(`Soul Duel: target not found: ${targetId}`);

    // Step 2: Guard: already pending (flag-on only checks this; flag-off uses applyDamage)
    if (isMinigameEnabled('soul-duel') && state.pendingSoulDuel !== null) return { success: false };

    // Step 3: Dead target
    if (target.isDead) return { success: false };

    // Step 4: Can't duel self
    if (targetId === casterId) return { success: false };

    // Step 5: Emit item_cast
    appendEvent(state, {
      type: 'item_cast',
      playerId: casterId,
      casterId,
      itemId: 'soul_duel',
      targetId,
    });

    if (tryBlockWithStoneSkin(state, targetId, { actorId: casterId, sourceId: 'soul_duel' })) {
      return { success: true, endsTurn: false, blockedByStoneSkin: true };
    }

    if (isMinigameEnabled('soul-duel')) {
      // Flag-on path: pick minigame via SEEDED RNG, set pending state
      const r = mulberry32(state.rngState);
      state.rngState = r.nextState;
      const idx = Math.floor(r.value * SOUL_DUEL_MINIGAME_POOL.length);
      const minigameId = SOUL_DUEL_MINIGAME_POOL[idx];

      state.pendingSoulDuel = { challengerId: casterId, defenderId: targetId, minigameId };

      appendEvent(state, {
        type: 'soul_duel_triggered',
        playerId: casterId,
        challengerId: casterId,
        defenderId: targetId,
        minigameId,
      });

      return { success: true, endsTurn: false };
    }

    // Flag-off fallback: 66/33 coin flip via Math.random (intentionally unseeded)
    // Pattern: applyDamage only (matches thunder-fist / orbital-cannon); game loop
    // calls handleDeath after item resolution if player.isDead === true.
    const attackerWins = Math.random() < 0.66;
    if (attackerWins) {
      applyDamage(state, targetId, 999, 'soul_duel', casterId);
    } else {
      applyDamage(state, casterId, 999, 'soul_duel_backfire', null);
    }

    return { success: true, endsTurn: false };
  },
};

// Self-register at module load
addItem(soulDuel);
